import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ImageIcon from '@mui/icons-material/Image';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SendIcon from '@mui/icons-material/Send';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  getWhatsAppTemplateHistory,
  TemplateHistoryBatch,
  TemplateHistoryRecipient,
  WhatsAppTemplate,
} from '../services/api';
import { statusChipColor } from './statusChipColor';

interface WhatsAppTemplateRowProps {
  template: WhatsAppTemplate;
  onSend: (template: WhatsAppTemplate) => void;
  onEdit: (template: WhatsAppTemplate) => void;
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Meta enforces a 24-hour cooldown between edits for APPROVED templates.
 * We approximate "last edit time" with the template's `updatedAt` field.
 */
const EDIT_COOLDOWN_MS = 24 * 60 * 60 * 1000;

function formatRemaining(ms: number): string {
  const totalMinutes = Math.ceil(ms / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours <= 0) return `${minutes}m`;
  if (minutes <= 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
}

export default function WhatsAppTemplateRow({
  template,
  onSend,
  onEdit,
}: WhatsAppTemplateRowProps) {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [batches, setBatches] = useState<TemplateHistoryBatch[]>([]);

  const status = (template.status || '').toUpperCase();
  const sendDisabled = ['REJECTED', 'DISABLED', 'PAUSED', 'DELETED', 'FAILED'].includes(status);

  // Meta only allows one edit every 24 hours per active template. We treat
  // `updatedAt === createdAt` as "never edited" so a freshly created or
  // freshly imported template can be edited immediately. The backend never
  // bumps `UpdatedAt` on background status refreshes, so this comparison
  // stays accurate after PENDING → APPROVED transitions.
  const editCooldown = useMemo(() => {
    if (!template.updatedAt) return { locked: false, remainingMs: 0 };
    const last = new Date(template.updatedAt).getTime();
    if (!Number.isFinite(last)) return { locked: false, remainingMs: 0 };
    const created = template.createdAt ? new Date(template.createdAt).getTime() : NaN;
    // Allow a small epsilon to absorb any sub-second drift between the two
    // writes when the row is first created.
    if (Number.isFinite(created) && Math.abs(last - created) < 2_000) {
      return { locked: false, remainingMs: 0 };
    }
    const remaining = EDIT_COOLDOWN_MS - (Date.now() - last);
    return remaining > 0
      ? { locked: true, remainingMs: remaining }
      : { locked: false, remainingMs: 0 };
  }, [template.updatedAt, template.createdAt]);
  const editTooltip = editCooldown.locked
    ? `Meta only allows one edit every 24 hours for active templates. Try again in ${formatRemaining(editCooldown.remainingMs)}.`
    : 'Edit template';
  // Action buttons (Edit + Send) only render once Meta has approved the
  // template — there's no point editing or broadcasting otherwise.
  const showActions = status === 'APPROVED';

  const loadHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getWhatsAppTemplateHistory(template.id);
      setBatches(result);
      setLoaded(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load history.');
    } finally {
      setLoading(false);
    }
  }, [template.id]);

  useEffect(() => {
    if (open && !loaded && !loading) {
      void loadHistory();
    }
  }, [open, loaded, loading, loadHistory]);

  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell sx={{ width: 48 }}>
          <IconButton
            aria-label={open ? 'Collapse history' : 'Expand history'}
            size="small"
            onClick={() => setOpen((prev) => !prev)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
            {template.name}
          </Typography>
          {template.hasImg && template.imageUrl && (
            <Tooltip title="This template carries an IMAGE header. The uploaded image is used as the runtime header on every broadcast.">
              <Chip
                size="small"
                variant="outlined"
                icon={<ImageIcon sx={{ fontSize: 16 }} />}
                label="Image header"
                sx={{ mt: 0.5, height: 22, '& .MuiChip-label': { px: 0.75 } }}
              />
            </Tooltip>
          )}
          {template.hasImg && !template.imageUrl && (
            <Tooltip title="Meta expects an IMAGE header for this template, but no image has been uploaded locally. Sends will be rejected until an image is attached via the edit form.">
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                icon={<WarningAmberIcon sx={{ fontSize: 16 }} />}
                label="Image required"
                sx={{ mt: 0.5, height: 22, '& .MuiChip-label': { px: 0.75 } }}
              />
            </Tooltip>
          )}
        </TableCell>
        <TableCell>{formatDate(template.createdAt)}</TableCell>
        <TableCell>{formatDate(template.updatedAt)}</TableCell>
        <TableCell>
          <Chip size="small" label={template.category} variant="outlined" />
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            label={status || 'UNKNOWN'}
            color={statusChipColor(status)}
          />
        </TableCell>
        <TableCell align="right">
          {showActions && (
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Tooltip title={editTooltip}>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => onEdit(template)}
                    disabled={editCooldown.locked}
                    sx={{ textTransform: 'none' }}
                  >
                    Edit
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Send to recipients">
                <span>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<SendIcon />}
                    onClick={() => onSend(template)}
                    disabled={sendDisabled}
                    sx={{
                      textTransform: 'none',
                      bgcolor: '#25D366',
                      '&:hover': { bgcolor: '#1DA851' },
                    }}
                  >
                    Send
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ py: 0, borderBottom: open ? undefined : 'none' }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, px: 1 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1.5 }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Sending history
                </Typography>
                <Button size="small" onClick={loadHistory} disabled={loading} sx={{ textTransform: 'none' }}>
                  Refresh
                </Button>
              </Stack>

              {loading && (
                <Stack alignItems="center" sx={{ py: 3 }}>
                  <CircularProgress size={20} />
                </Stack>
              )}
              {error && (
                <Typography variant="body2" sx={{ color: 'error.main' }}>
                  {error}
                </Typography>
              )}
              {!loading && !error && batches.length === 0 && loaded && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  No sends yet for this template.
                </Typography>
              )}
              {!loading && !error && batches.map((batch) => (
                <BatchHistoryCard key={batch.uuid} batch={batch} />
              ))}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

interface BatchHistoryCardProps {
  batch: TemplateHistoryBatch;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50];

/**
 * Renders one batch's recipients as a paginated table. Pagination state is
 * scoped per batch so collapsing/refreshing other batches does not reset it.
 */
function BatchHistoryCard({ batch }: BatchHistoryCardProps) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(PAGE_SIZE_OPTIONS[0]);

  const recipients: TemplateHistoryRecipient[] = batch.recipients;
  const total = recipients.length;
  const start = page * rowsPerPage;
  const visible = recipients.slice(start, start + rowsPerPage);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Accordion
      disableGutters
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        mb: 2,
        '&:before': { display: 'none' },
        overflow: 'hidden',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          bgcolor: 'action.hover',
          px: 2,
          '& .MuiAccordionSummary-content': {
            my: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
          },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Batch sent {formatDate(batch.sentAt)}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', fontFamily: 'monospace' }}
          >
            {batch.uuid}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={`${batch.recipientCount} recipient${batch.recipientCount === 1 ? '' : 's'}`}
          sx={{ mr: 1 }}
        />
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>CEA number</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Mobile</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Purpose</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Sent at</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((r) => (
              <TableRow key={r.sentId}>
                <TableCell>{r.ceaNumber || '—'}</TableCell>
                <TableCell>{r.mobile || '—'}</TableCell>
                <TableCell>{r.purpose || '—'}</TableCell>
                <TableCell>{formatDate(r.sentAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {total > PAGE_SIZE_OPTIONS[0] && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
}
